import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

function formatProxyAge(listedDays) {
    if (listedDays == null || listedDays === '') return 'N/A'
    const days = Number(listedDays)
    if (!Number.isFinite(days)) return 'N/A'
    if (days >= 365) return `${Math.floor(days / 365)}y`
    if (days >= 30) return `${Math.floor(days / 30)}m`
    return `${days}d`
}

function maturityOf(listedDays) {
    const days = Number(listedDays)
    if (!Number.isFinite(days) || days < 3) return 'new'
    if (days < 7) return 'warming'
    return 'stable'
}

function hoursLabel(uptime) {
    const value = Math.round((Number(uptime) / 100) * 24 * 10) / 10
    if (!Number.isFinite(value)) return '0'
    return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function listedPhrase(listedDays) {
    const days = Math.max(0, Math.round(Number(listedDays) || 0))
    if (days >= 365) {
        const years = Math.floor(days / 365)
        return years === 1 ? '1 year' : `${years} years`
    }
    if (days >= 30) {
        const months = Math.floor(days / 30)
        return months === 1 ? '1 month' : `${months} months`
    }
    return days === 1 ? '1 day' : `${days} days`
}

export function activityTip(listedDays, uptime) {
    const state = maturityOf(listedDays)
    const pct = Math.round(Number(uptime) || 0)
    const hours = hoursLabel(pct)
    if (state === 'new') {
        return `This proxy was just added to the catalog. ${pct}% does not mean it is stable yet — there has not been enough time to see how often it stays online.`
    }
    if (state === 'warming') {
        return `Listed for ${listedPhrase(listedDays)}. ${pct}% is about ${hours} hours a day, but the sample is still short.`
    }
    return `Listed for ${listedPhrase(listedDays)}. ${pct}% means about ${hours} hours online per day. This figure has had time to settle.`
}

function HelpButton({ listedDays, uptime }) {
    const btnRef = useRef(null)
    const [open, setOpen] = useState(false)
    const [pos, setPos] = useState(null)

    const place = () => {
        const rect = btnRef.current?.getBoundingClientRect()
        if (!rect) return
        setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 })
    }

    useEffect(() => {
        if (!open) return undefined
        const close = () => setOpen(false)
        window.addEventListener('scroll', close, true)
        window.addEventListener('resize', close)
        return () => {
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('resize', close)
        }
    }, [open])

    const tip = open && pos
        ? createPortal(
            <div
                className="proxy-maturity__tip"
                style={{ top: pos.top, left: pos.left }}
                role="tooltip"
            >
                {activityTip(listedDays, uptime)}
            </div>,
            document.body
        )
        : null

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className="proxy-maturity__help"
                aria-label="About age and activity"
                onMouseEnter={() => {
                    place()
                    setOpen(true)
                }}
                onMouseLeave={() => setOpen(false)}
                onFocus={() => {
                    place()
                    setOpen(true)
                }}
                onBlur={() => setOpen(false)}
                onClick={(event) => {
                    event.stopPropagation()
                    if (window.matchMedia('(hover: none)').matches) {
                        place()
                        setOpen((value) => !value)
                    }
                }}
            >
                ?
            </button>
            {tip}
        </>
    )
}

const ProxyAgeActivity = ({ listedDays, uptime, mode = 'combined' }) => {
    if (uptime === undefined || uptime === null) {
        return <span className="proxy-maturity proxy-maturity--new">N/A</span>
    }

    const state = maturityOf(listedDays)
    const age = formatProxyAge(listedDays)
    const pct = `${Math.round(Number(uptime))}%`
    const pill = state === 'new' ? <span className="proxy-maturity__pill">New</span> : null

    if (mode === 'age') {
        return (
            <span className={`proxy-maturity proxy-maturity--${state}`}>
                <span>{age}</span>
                {pill}
            </span>
        )
    }

    if (mode === 'activity') {
        return (
            <span className={`proxy-maturity proxy-maturity--${state}`}>
                <span>{pct}</span>
                <HelpButton listedDays={listedDays} uptime={uptime} />
            </span>
        )
    }

    return (
        <span className={`proxy-maturity proxy-maturity--${state}`}>
            <span>{age}/{pct}</span>
            {pill}
            <HelpButton listedDays={listedDays} uptime={uptime} />
        </span>
    )
}

export default ProxyAgeActivity
